import React, { useCallback } from "react";
import { useFormik } from "formik";

import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";

import { useAppContext } from "../../api/AppContext";
import SaveButtons from "../SaveButtons";
import InfoTooltip from "../InfoTooltip";
import { multilineInputProps } from "../utils";

export const parseDidList = (raw) => [
  ...new Set(
    raw
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
  ),
];

export const formatDidList = (dids) => (dids || []).join("\n");

const Simulation = () => {
  const { updateSimulationPollingAllowed } = useAppContext();
  const minVisibleRows = 12;
  const maxVisibleRows = 20;

  const formik = useFormik({
    initialValues: {
      deviceIds: "",
    },
    onSubmit: async (values, { resetForm }) => {
      const dids = parseDidList(values.deviceIds);
      const result = await updateSimulationPollingAllowed({ dids });
      resetForm({
        values: { deviceIds: formatDidList(result.dids) },
      });
    },
  });

  const deviceIdCount = parseDidList(formik.values.deviceIds).length;
  const inputLineCount = formik.values.deviceIds
    ? formik.values.deviceIds.split("\n").length
    : 1;
  const textareaRows = Math.min(
    maxVisibleRows,
    Math.max(minVisibleRows, inputLineCount + 1)
  );

  const handleDeviceIdsChange = useCallback(
    (e) => {
      formik.setFieldValue("deviceIds", e.target.value);
      formik.setFieldTouched("deviceIds", true);
    },
    [formik]
  );

  return (
    <Box
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <Typography variant="h6">Simulation</Typography>
            </Grid>
            <Grid item>
              <InfoTooltip
                text="Saving enables polling for the DIDs entered below. Already-enabled DIDs are refreshed. DIDs not entered are left unchanged until the user deactivates them or they auto-deactivate. Clients must restart HI for the change to take effect."
                title="Simulation polling"
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography color="textSecondary" variant="body2">
            Enter device IDs (DIDs) that should have polling enabled, separated
            by a new line or comma.
          </Typography>
        </Grid>

        <Grid item md={8} xs={12}>
          <TextField
            fullWidth
            multiline
            InputLabelProps={{ shrink: true }}
            InputProps={multilineInputProps}
            helperText={`${deviceIdCount} device ID${
              deviceIdCount === 1 ? "" : "s"
            }`}
            label="Device IDs to enable for polling"
            margin="none"
            placeholder={"device-id-1\ndevice-id-2"}
            rows={textareaRows}
            value={formik.values.deviceIds}
            variant="outlined"
            onChange={handleDeviceIdsChange}
          />
        </Grid>

        <Grid item xs={12}>
          <SaveButtons formik={formik} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Simulation;
